import React, { useRef } from "react";
import { UploadCloud, FileType, X } from "lucide-react";

interface Props {
  file: File | null;
  onFileChange: (file: File | null) => void;
}

const FileUploadTab: React.FC<Props> = ({ file, onFileChange }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      onFileChange(e.target.files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => e.preventDefault();
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files?.[0]) {
      onFileChange(e.dataTransfer.files[0]);
    }
  };

  const clearFile = () => {
    onFileChange(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
      {!file ? (
        <div
          className="border-2 border-dashed border-slate-300 rounded-xl p-10 text-center hover:bg-slate-50 hover:border-indigo-400 transition-all cursor-pointer group"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            type="file"
            className="hidden"
            ref={fileInputRef}
            accept=".pdf,.docx,.txt"
            onChange={handleFileChange}
          />
          <div className="w-16 h-16 bg-indigo-50 text-indigo-500 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
            <UploadCloud size={32} />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-1">
            Click to upload or drag and drop
          </h3>
          <p className="text-sm text-slate-500">PDF, DOCX, or TXT (Max 10MB)</p>
        </div>
      ) : (
        <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 flex items-center justify-between animate-in zoom-in-95">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center text-indigo-600 shadow-sm">
              <FileType size={24} />
            </div>
            <div>
              <p className="font-semibold text-slate-900">{file.name}</p>
              <p className="text-sm text-slate-500">
                {(file.size / 1024).toFixed(2)} KB
              </p>
            </div>
          </div>
          <button
            onClick={clearFile}
            className="p-2 hover:bg-white hover:text-red-500 rounded-full transition-colors text-slate-400"
          >
            <X size={20} />
          </button>
        </div>
      )}
    </div>
  );
};

export default FileUploadTab;
